import React, { useEffect, useState } from 'react';
import {
  Paper,
  Typography,
  Button,
  Divider,
  Checkbox,
  IconButton,
  InputBase,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import {
  Autocomplete,
  TextField,
  Switch,
  LinearProgress,
  CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { CompanyData, CourseData, SharedCoursesData } from 'services/dataService';
import { useStyles } from './styles';

const STATUS_COMPANY_ACTIVE = 1;
const CHUNK_SIZE = 20;

const defaultConfig = {
  signable: true,
  allow_public_access: false,
  require_classes_completion: true,
  require_classes_approval: true,
  published: true,
};

function chunk(array, size) {
  const chunks = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.common.black,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  '&:nth-of-type(odd)': {
    backgroundColor: '#F6F9FC',
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

export default () => {
  const classes = useStyles();
  const { t } = useTranslation(['toastify']);
  const { id_company: mainCompanyId } = useSelector((state) => state.auth);

  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [rows, setRows] = useState([]);
  const [name, setName] = useState('');
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);

  const [sharing, setSharing] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [summary, setSummary] = useState(null);

  async function fetchCompanies() {
    try {
      setLoadingCompanies(true);
      const response = await CompanyData.getSubplataforms({
        status: STATUS_COMPANY_ACTIVE,
        limit: 1000,
      });
      const companies = (response.data.companies || []).filter(
        (company) => company.id !== mainCompanyId
      );
      setRows(
        companies.map((company) => ({
          id: company.id,
          name: company.name,
          domain: company.domain,
          selected: true,
          ...defaultConfig,
        }))
      );
    } catch (e) {
      toast.error(t('toastify:commons.toast_error_api'));
    } finally {
      setLoadingCompanies(false);
    }
  }

  async function fetchCourses() {
    try {
      setLoadingCourses(true);
      const response = await CourseData.getCourses({ limit: 500 });
      const courseRows = (response.data.courses || []).filter((row) => row.course);
      setCourses(courseRows);
    } catch (e) {
      toast.error(t('toastify:commons.toast_error_api'));
    } finally {
      setLoadingCourses(false);
    }
  }

  useEffect(() => {
    fetchCompanies();
    fetchCourses();
  }, []);

  const filteredRows = rows.filter((row) =>
    row.name.toLowerCase().includes(name.toLowerCase())
  );

  const allFilteredSelected =
    filteredRows.length > 0 && filteredRows.every((row) => row.selected);

  const selectedCount = rows.filter((row) => row.selected).length;

  function updateRow(id, patch) {
    setSummary(null);
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  }

  function toggleAll() {
    const filteredIds = filteredRows.map((row) => row.id);
    setSummary(null);
    setRows((prev) =>
      prev.map((row) =>
        filteredIds.includes(row.id) ? { ...row, selected: !allFilteredSelected } : row
      )
    );
  }

  async function handleShare() {
    if (!selectedCourse) {
      toast.error('Selecione um curso para compartilhar.');
      return;
    }

    const selectedRows = rows.filter((row) => row.selected);

    if (selectedRows.length === 0) {
      toast.error('Selecione ao menos uma subplataforma.');
      return;
    }

    const payloadCompanies = selectedRows.map((row) => ({
      id_company: row.id,
      signable: row.signable,
      allow_public_access: row.allow_public_access,
      require_classes_completion: row.require_classes_completion,
      require_classes_approval: row.require_classes_approval,
      published: row.published,
    }));

    const batches = chunk(payloadCompanies, CHUNK_SIZE);
    const createdIds = [];
    const skippedIds = [];
    const failedItems = [];

    setSharing(true);
    setSummary(null);
    setProgress({ done: 0, total: batches.length });

    for (const batch of batches) {
      try {
        const response = await SharedCoursesData.shareCourseWithCompanies({
          id_course: selectedCourse.id_course,
          companies: batch,
        });
        createdIds.push(...(response.data?.created || []));
        skippedIds.push(...(response.data?.skipped || []));
        (response.data?.failed || []).forEach((item) =>
          failedItems.push({ id: item.id_company, error: item.error })
        );
      } catch (e) {
        batch.forEach((company) =>
          failedItems.push({ id: company.id_company, error: 'Falha na requisição' })
        );
      } finally {
        setProgress((prev) => ({ ...prev, done: prev.done + 1 }));
      }
    }

    const nameById = Object.fromEntries(rows.map((row) => [row.id, row.name]));

    setSharing(false);
    setSummary({
      created: createdIds.map((id) => ({ id, name: nameById[id] || id })),
      skipped: skippedIds.map((id) => ({ id, name: nameById[id] || id })),
      failed: failedItems.map((item) => ({
        id: item.id,
        name: nameById[item.id] || item.id,
        error: item.error,
      })),
    });

    if (failedItems.length > 0) {
      toast.error(
        `Compartilhado com ${createdIds.length} subplataforma(s). ${skippedIds.length} já possuíam o curso. ${failedItems.length} falharam.`
      );
    } else {
      toast.success(
        `Compartilhado com ${createdIds.length} subplataforma(s). ${skippedIds.length} já possuíam o curso.`
      );
    }
  }

  return (
    <Paper className={classes.container}>
      <Typography variant="h4" className={classes.sectionTitle}>
        Compartilhar Curso com Subplataformas
      </Typography>

      <div className={classes.section}>
        <Typography variant="subtitle2" className={classes.sectionTitle}>
          Curso da plataforma mãe
        </Typography>
        <Autocomplete
          fullWidth
          loading={loadingCourses}
          options={courses}
          value={selectedCourse}
          isOptionEqualToValue={(option, value) => option.id_course === value.id_course}
          getOptionLabel={(option) => option?.course?.name || ''}
          onChange={(event, value) => {
            setSummary(null);
            setSelectedCourse(value);
          }}
          renderInput={(params) => (
            <TextField {...params} label="Buscar curso" variant="outlined" />
          )}
        />
      </div>

      <Divider />

      <div className={classes.section}>
        <div className={classes.tableHeader}>
          <Typography variant="subtitle2">
            Subplataformas ativas ({selectedCount} de {rows.length} selecionadas)
          </Typography>
          {loadingCompanies && <CircularProgress size={20} />}
        </div>

        <div className={classes.searchBarContainer}>
          <Paper component="form" className={classes.root}>
            <InputBase
              className={classes.input}
              placeholder="Pesquisar"
              inputProps={{ 'aria-label': 'search' }}
              onChange={(e) => setName(e.currentTarget.value)}
            />
            <IconButton className={classes.iconButton} aria-label="search">
              <SearchIcon />
            </IconButton>
          </Paper>
        </div>

        <TableContainer>
          <Table sx={{ minWidth: 700 }} aria-label="customized table">
            <TableHead>
              <TableRow>
                <StyledTableCell align="center">
                  <Checkbox
                    checked={allFilteredSelected}
                    onChange={toggleAll}
                    inputProps={{ 'aria-label': 'primary checkbox' }}
                    size="medium"
                    color="primary"
                  />
                </StyledTableCell>
                <StyledTableCell align="center">Subplataforma</StyledTableCell>
                <StyledTableCell align="center">Assinável</StyledTableCell>
                <StyledTableCell align="center">Acesso público via CPF</StyledTableCell>
                <StyledTableCell align="center">Aprovação</StyledTableCell>
                <StyledTableCell align="center">Conclusão</StyledTableCell>
                <StyledTableCell align="center">Publicado</StyledTableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredRows.map((row) => (
                <StyledTableRow key={row.id}>
                  <StyledTableCell align="center">
                    <Checkbox
                      checked={row.selected}
                      onChange={() => updateRow(row.id, { selected: !row.selected })}
                      inputProps={{ 'aria-label': 'primary checkbox' }}
                      size="medium"
                      color="primary"
                    />
                  </StyledTableCell>
                  <StyledTableCell component="th" scope="row">
                    {row.name}
                  </StyledTableCell>
                  <StyledTableCell align="center">
                    <Switch
                      checked={row.signable}
                      onChange={() => updateRow(row.id, { signable: !row.signable })}
                    />
                  </StyledTableCell>
                  <StyledTableCell align="center">
                    <Switch
                      checked={row.allow_public_access}
                      onChange={() =>
                        updateRow(row.id, { allow_public_access: !row.allow_public_access })
                      }
                    />
                  </StyledTableCell>
                  <StyledTableCell align="center">
                    <Switch
                      checked={row.require_classes_approval}
                      onChange={() =>
                        updateRow(row.id, {
                          require_classes_approval: !row.require_classes_approval,
                        })
                      }
                    />
                  </StyledTableCell>
                  <StyledTableCell align="center">
                    <Switch
                      checked={row.require_classes_completion}
                      onChange={() =>
                        updateRow(row.id, {
                          require_classes_completion: !row.require_classes_completion,
                        })
                      }
                    />
                  </StyledTableCell>
                  <StyledTableCell align="center">
                    <Switch
                      checked={row.published}
                      onChange={() => updateRow(row.id, { published: !row.published })}
                    />
                  </StyledTableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      <div className={classes.actions}>
        <Button
          variant="contained"
          color="primary"
          disabled={sharing || loadingCompanies || loadingCourses}
          onClick={handleShare}
        >
          {sharing ? 'Compartilhando...' : 'Compartilhar'}
        </Button>
        {sharing && (
          <div className={classes.progress}>
            <LinearProgress
              variant="determinate"
              value={progress.total ? (progress.done / progress.total) * 100 : 0}
            />
            <Typography variant="caption">
              Lote {progress.done} de {progress.total}
            </Typography>
          </div>
        )}
      </div>

      {!sharing && summary && (
        <div className={classes.summary}>
          <div>
            <Typography variant="subtitle2">
              Compartilhado com {summary.created.length} subplataforma(s)
            </Typography>
            {summary.created.length > 0 && (
              <ul className={classes.summaryList}>
                {summary.created.map((item) => (
                  <li key={item.id}>
                    <Typography variant="body2">{item.name}</Typography>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {summary.skipped.length > 0 && (
            <div>
              <Typography variant="subtitle2">
                Já possuíam o curso ({summary.skipped.length})
              </Typography>
              <ul className={classes.summaryList}>
                {summary.skipped.map((item) => (
                  <li key={item.id}>
                    <Typography variant="body2">{item.name}</Typography>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {summary.failed.length > 0 && (
            <div>
              <Typography variant="subtitle2" color="error">
                Falharam ({summary.failed.length})
              </Typography>
              <ul className={classes.summaryList}>
                {summary.failed.map((item) => (
                  <li key={item.id}>
                    <Typography variant="body2" color="error">
                      {item.name} — {item.error}
                    </Typography>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </Paper>
  );
};
